import {
  ChartFrequency,
  IComposableChartsWidget,
  IPostgreJobsAnalyticsPayload,
} from 'wisestep-api';

import {
  ClientNotesBarChartPostgre,
  ClientNotesMetricChartPostgre,
  ClientNotesTablePostgre,
} from '../../../../composableAnalytics/BaseCharts';
import { ChartScope } from '../../../types';
import { ClientNotesWidget, getChartFreqForTimePeriod } from '../../helper';
import { jobsTimePeriodList } from '../../Jobs/helper';

const getClientNotesChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Record<ClientNotesWidget, IComposableChartsWidget> => {
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'client_notes',
    filters: [],
    type: 'count-metric',
    range: timePeriodId,
    scope,
    frequency,
  };

  const widgetConfig = {
    [ClientNotesWidget.METRIC_TOTAL]: {
      name: 'Total Notes (Client & Client Contact)',
      filters: {},
      category: 'client_notes',
      type: `metric_totalnotes_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesMetricChartPostgre
          query={{ ...defaultQuery, type: 'total-metric' }}
          title="Total Notes (Client & Client Contact)"
          widgetName={ClientNotesWidget.METRIC_TOTAL}
        />
      ),
    },
    [ClientNotesWidget.METRIC_TOTAL_TYPE_WISE]: {
      name: 'Total Notes by Type',
      filters: {},
      category: 'client_notes',
      type: `metric_type_totalnotes_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesMetricChartPostgre
          query={{ ...defaultQuery, type: 'total-metric-type-wise' }}
          title="Total Notes by Type"
          widgetName={ClientNotesWidget.METRIC_TOTAL_TYPE_WISE}
        />
      ),
    },
    [ClientNotesWidget.METRIC_TOTAL_CLIENT]: {
      name: 'Total Client Notes',
      filters: {},
      category: 'client_notes',
      type: `metric_clientnotes_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesMetricChartPostgre
          query={{ ...defaultQuery, type: 'client-metric' }}
          title="Total Client Notes"
          widgetName={ClientNotesWidget.METRIC_TOTAL_CLIENT}
        />
      ),
    },
    [ClientNotesWidget.METRIC_TOTAL_CLIENT_SPOC]: {
      name: 'Total Client Contact Notes',
      filters: {},
      category: 'client_notes',
      type: `metric_clientspocnotes_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesMetricChartPostgre
          query={{ ...defaultQuery, type: 'client-contact-metric' }}
          title="Total Client Contact Notes"
          widgetName={ClientNotesWidget.METRIC_TOTAL_CLIENT_SPOC}
        />
      ),
    },
    [ClientNotesWidget.TABLE_CLIENT]: {
      name: 'Client-Wise Notes',
      filters: {},
      category: 'client_notes',
      type: `table_clientnotes_client_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'client-table' }}
          title="Client-Wise Notes"
          widgetName={ClientNotesWidget.TABLE_CLIENT}
          widgetType={`table_clientnotes_client_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.TABLE_CLIENT_SPOC]: {
      name: 'Client Contact-Wise Notes',
      filters: {},
      category: 'client_notes',
      type: `table_clientspocnotes_client_spoc_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'client-contact-table' }}
          title="Client Contact-Wise Notes"
          widgetName={ClientNotesWidget.TABLE_CLIENT_SPOC}
          widgetType={`table_clientspocnotes_client_spoc_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.TABLE_RECRUITER_CLIENT]: {
      name: 'Client Notes Created by',
      filters: {},
      category: 'client_notes',
      type: `table_clientnotes_recruiter_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'recruiter-client-table' }}
          title="Client Notes Created by"
          widgetName={ClientNotesWidget.TABLE_RECRUITER_CLIENT}
          widgetType={`table_clientnotes_recruiter_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.TABLE_RECRUITER_CLIENT_SPOC]: {
      name: 'Client Contact Notes Created by',
      filters: {},
      category: 'client_notes',
      type: `table_clientspocnotes_recruiter_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'recruiter-client-contact-table' }}
          title="Client Contact Notes Created by"
          widgetName={ClientNotesWidget.TABLE_RECRUITER_CLIENT_SPOC}
          widgetType={`table_clientspocnotes_recruiter_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.TABLE_TYPEWISE_CLIENT]: {
      name: 'Client Notes by Type',
      filters: {},
      category: 'client_notes',
      type: `table_clientnotes_typewise_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'type-client-table' }}
          title="Client Notes by Type"
          widgetName={ClientNotesWidget.TABLE_TYPEWISE_CLIENT}
          widgetType={`table_clientnotes_typewise_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.TABLE_TYPEWISE_CLIENT_SPOC]: {
      name: 'Client Contact Notes by Type',
      filters: {},
      category: 'client_notes',
      type: `table_clientspocnotes_typewise_${timePeriodId}_${scope}_postgre`,
      component: (
        <ClientNotesTablePostgre
          query={{ ...defaultQuery, type: 'type-client-contact-table' }}
          title="Client Contact Notes by Type"
          widgetName={ClientNotesWidget.TABLE_TYPEWISE_CLIENT_SPOC}
          widgetType={`table_clientspocnotes_typewise_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [ClientNotesWidget.BAR_CHART_CLIENT]: {
      name: `Client Notes Trends - ${frequency}`,
      filters: {},
      category: 'client_notes',
      type: `barchart_clientnotes_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <ClientNotesBarChartPostgre
          query={{ ...defaultQuery, type: 'client-bar-chart' }}
          title={`Client Notes Trends ${frequency}`}
          widgetName={ClientNotesWidget.BAR_CHART_CLIENT}
        />
      ),
    },
    [ClientNotesWidget.BAR_CHART_CLIENT_SPOC]: {
      name: `Client Contact Notes Trends - ${frequency}`,
      filters: {},
      category: 'client_notes',
      type: `barchart_clientspocnotes_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <ClientNotesBarChartPostgre
          query={{ ...defaultQuery, type: 'client-contact-bar-chart' }}
          title={`Client Contact Notes Trends ${frequency}`}
          widgetName={ClientNotesWidget.BAR_CHART_CLIENT_SPOC}
        />
      ),
    },
  };

  return widgetConfig;
};

export const clientNotesChartsKeyPostgre = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(
    getClientNotesChartWidgetByTimeAndScope(timePeriodId, scope)
  );
  const extraWidgetList = {};

  return [...defaultWidgetList, ...(extraWidgetList[timePeriodId] ?? [])];
};

export const postgreClientNotesDashboardKeysObjectList = jobsTimePeriodList.flatMap(
  (timePeriod) => [
    ...clientNotesChartsKeyPostgre(timePeriod.id, ChartScope.WORKSPACE),
    ...clientNotesChartsKeyPostgre(timePeriod.id, ChartScope.LOGGED_IN_USER),
  ]
);
